import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Empty, Button, Spin } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PhotoGallery, { PhotoGallery as PhotoGalleryComponent } from '../components/PhotoGallery';
import { DateRange } from '../components/DateRangeSelector';
import { useDevice } from '../contexts/DeviceContext';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import './PhotoPage.css';

interface PhotoData {
  id: string;
  captured_at: string;
  received_at: string;
  file_size: number;
  status: string;
  image_width?: number;
  image_height?: number;
  camera_make?: string;
  camera_model?: string;
  gps_latitude?: number;
  gps_longitude?: number;
  imu_orientation?: number;
  thumbnailUrl?: string;
  image_data?: string;
  timestamp?: string;
  latitude?: number;
  longitude?: number;
  direction?: number;
}

interface PhotoTimelineMonth {
  month: string;
  count: number;
}

interface PhotoTimelineDay {
  date: string;
  count: number;
}

interface PhotoTimelineResponse {
  min_date?: string | null;
  max_date?: string | null;
  months: PhotoTimelineMonth[];
  days?: PhotoTimelineDay[];
}

const DEFAULT_PAGE_SIZE = 60;
const TIMELINE_WIDTH = 160;
const HEADER_HEIGHT_ESTIMATE = 240;
const toMonthKeyUtc = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};
const monthKeyToIndex = (key: string) => {
  const [year, month] = key.split('-').map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return null;
  }
  return year * 12 + (month - 1);
};
const monthKeyToMidIso = (key: string) => {
  const [year, month] = key.split('-').map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return null;
  }
  return new Date(Date.UTC(year, month - 1, 15, 12)).toISOString();
};

const calcPageSize = (columnWidth: number) => {
  const availableWidth = Math.max(window.innerWidth - TIMELINE_WIDTH - 64, columnWidth);
  const columns = Math.max(1, Math.floor(availableWidth / columnWidth));
  const tileHeight = columnWidth * 0.75 + 28;
  const availableHeight = Math.max(window.innerHeight - HEADER_HEIGHT_ESTIMATE, tileHeight * 2);
  const rows = Math.max(2, Math.floor(availableHeight / tileHeight));
  return Math.max(columns * rows * 2, DEFAULT_PAGE_SIZE);
};
const MIN_COLUMN_WIDTH = 140;
const MAX_COLUMN_WIDTH = 260;
const COLUMN_WIDTH_STEP = 12;

const PhotoPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { deviceId: routeDeviceId } = useParams<{ deviceId?: string }>();
  const navigate = useNavigate();
  const { devices, selectedDevice, setSelectedDevice } = useDevice();
  const [photoPageData, setPhotoPageData] = useState<PhotoData[]>([]);
  const [photoTotal, setPhotoTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasMorePrev, setHasMorePrev] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [startOffset, setStartOffset] = useState(0);
  const [timeline, setTimeline] = useState<PhotoTimelineResponse | null>(null);
  const [columnWidth, setColumnWidth] = useState(180);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const [selectedTimelineAt, setSelectedTimelineAt] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
    container: HTMLDivElement | null;
  } | null>(null);
  const reloadKeyRef = useRef<string | null>(null);
  const suppressReloadRef = useRef(false);
  const lastAnchorKeyRef = useRef<string | null>(null);
  const anchorRequestIdRef = useRef(0);
  const listRequestIdRef = useRef(0);
  const listScrollInFlightRef = useRef(false);
  const timelineModeRef = useRef(false);
  const [deviceNotFound, setDeviceNotFound] = useState(false);
  const searchParamString = useMemo(() => searchParams.toString(), [searchParams]);
  const activeDeviceId = routeDeviceId ?? selectedDevice?.id ?? null;
  const normalizeTimelineAnchor = useCallback(
    (at: string) => {
      if (!timeline?.months?.length) {
        return at;
      }
      const targetKey = toMonthKeyUtc(at);
      if (!targetKey) {
        return at;
      }
      const activeMonths = timeline.months
        .filter((month) => (Number(month.count) || 0) > 0)
        .map((month) => (month.month.length > 7 ? month.month.slice(0, 7) : month.month));
      if (activeMonths.length === 0) {
        return at;
      }
      if (activeMonths.includes(targetKey)) {
        return at;
      }
      const targetIndex = monthKeyToIndex(targetKey);
      if (targetIndex === null) {
        return at;
      }
      let bestKey = activeMonths[0];
      let bestIndex = monthKeyToIndex(bestKey) ?? targetIndex;
      let bestDistance = Math.abs(bestIndex - targetIndex);
      activeMonths.forEach((monthKey) => {
        const monthIndex = monthKeyToIndex(monthKey);
        if (monthIndex === null) {
          return;
        }
        const distance = Math.abs(monthIndex - targetIndex);
        if (distance < bestDistance || (distance === bestDistance && monthIndex > bestIndex)) {
          bestKey = monthKey;
          bestIndex = monthIndex;
          bestDistance = distance;
        }
      });
      return monthKeyToMidIso(bestKey) ?? at;
    },
    [timeline],
  );

  const parseRangeFromParams = useCallback((): DateRange | null => {
    const params = new URLSearchParams(searchParamString);
    const startParam = params.get('start_time');
    const endParam = params.get('end_time');
    if (startParam && endParam && !Number.isNaN(Date.parse(startParam)) && !Number.isNaN(Date.parse(endParam))) {
      return { start: new Date(startParam).toISOString(), end: new Date(endParam).toISOString() };
    }
    return null;
  }, [searchParamString]);

  const parseNumberParam = useCallback(
    (key: string, fallback: number, min = 1) => {
      const params = new URLSearchParams(searchParamString);
      const value = Number(params.get(key));
      if (Number.isFinite(value) && value >= min) {
        return Math.floor(value);
      }
      return fallback;
    },
    [searchParamString],
  );

  const [currentRange, setCurrentRange] = useState<DateRange | null>(() => parseRangeFromParams());
  const [pageSize, setPageSize] = useState<number>(() => parseNumberParam('page_size', calcPageSize(180)));
  const [isParamsInitialized, setParamsInitialized] = useState(false);

  useEffect(() => {
    if (!routeDeviceId) {
      setDeviceNotFound(false);
      return;
    }
    if (devices.length === 0) {
      setDeviceNotFound(false);
      return;
    }
    const match = devices.find((device) => device.id === routeDeviceId) || null;
    if (match) {
      setDeviceNotFound(false);
      if (selectedDevice?.id !== match.id) {
        setSelectedDevice(match);
      }
    } else {
      setDeviceNotFound(true);
    }
  }, [routeDeviceId, devices, selectedDevice, setSelectedDevice]);

  const updateQueryParams = useCallback(
    (range: DateRange | null, size: number) => {
      const params = new URLSearchParams(searchParamString);
      let changed = false;
      if (range?.start && range?.end) {
        if (params.get('start_time') !== range.start) {
          params.set('start_time', range.start);
          changed = true;
        }
        if (params.get('end_time') !== range.end) {
          params.set('end_time', range.end);
          changed = true;
        }
      } else {
        if (params.has('start_time')) {
          params.delete('start_time');
          changed = true;
        }
        if (params.has('end_time')) {
          params.delete('end_time');
          changed = true;
        }
      }
      if (params.get('page_size') !== String(size)) {
        params.set('page_size', String(size));
        changed = true;
      }
      if (params.has('page')) {
        params.delete('page');
        changed = true;
      }
      if (changed) {
        setSearchParams(params, { replace: true });
      }
    },
    [searchParamString, setSearchParams],
  );

  const mergePhotos = useCallback((base: PhotoData[], incoming: PhotoData[], mode: 'append' | 'prepend') => {
    const seen = new Set(base.map((item) => item.id));
    const next = mode === 'append' ? [...base] : [];
    if (mode === 'prepend') {
      incoming.forEach((item) => {
        if (!seen.has(item.id)) {
          next.push(item);
          seen.add(item.id);
        }
      });
      base.forEach((item) => {
        if (!seen.has(item.id)) {
          next.push(item);
          seen.add(item.id);
        }
      });
      return next;
    }
    incoming.forEach((item) => {
      if (!seen.has(item.id)) {
        next.push(item);
        seen.add(item.id);
      }
    });
    return next;
  }, []);

  const normalizePhoto = useCallback((photo: Record<string, any>): PhotoData => {
    const thumbnailUrl = photo.thumbnailUrl ?? photo.thumbnail_url ?? photo.thumbnail;
    return {
      ...photo,
      thumbnailUrl,
    } as PhotoData;
  }, []);

  const normalizePhotoList = useCallback(
    (photos: Record<string, any>[]) => photos.map((photo) => normalizePhoto(photo)),
    [normalizePhoto],
  );

  const fetchPhotoData = useCallback(
    async (
      deviceId: string,
      range: DateRange | null,
      offset: number,
      size: number,
      mode: 'replace' | 'append' | 'prepend',
    ) => {
      const requestId = ++listRequestIdRef.current;
      if (mode === 'append') {
        setLoadingMore(true);
      } else if (mode === 'prepend') {
        setLoadingPrev(true);
      } else {
        setLoading(true);
      }
      try {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();

        if (!token) {
          secureLogger.error('No token for photos request');
          return;
        }

        const limit = size > 0 ? size : DEFAULT_PAGE_SIZE;
        const nextOffsetValue = Math.max(0, offset);
        const params = new URLSearchParams();
        if (range?.start && range?.end) {
          params.set('from_time', range.start);
          params.set('to_time', range.end);
        }
        params.set('limit', String(limit));
        params.set('offset', String(nextOffsetValue));
        const query = params.toString();
        const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/photos${query ? `?${query}` : ''}`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (requestId !== listRequestIdRef.current) {
            return;
          }
          const photos = normalizePhotoList(data.photos || []);
          const hasTotal = typeof data.total === 'number' && Number.isFinite(data.total);
          const hasMoreFlag = typeof data.has_more === 'boolean' ? data.has_more : null;
          const total = hasTotal ? data.total : nextOffsetValue + photos.length;
          const newEndOffset = nextOffsetValue + photos.length;
          const currentStartOffset = startOffset;
          const currentNextOffset = nextOffset;
          let newStartOffset = currentStartOffset;
          let newNextOffset = currentNextOffset;
          if (mode === 'replace') {
            newStartOffset = nextOffsetValue;
            newNextOffset = newEndOffset;
          } else if (mode === 'append') {
            newStartOffset = currentStartOffset;
            newNextOffset = newEndOffset;
          } else {
            newStartOffset = nextOffsetValue;
            newNextOffset = currentNextOffset;
          }
          const maxItems = Math.max(pageSize * 4, DEFAULT_PAGE_SIZE);
          let trimmedFromStart = 0;
          let trimmedFromEnd = 0;

          setPhotoPageData((prev) => {
            let next = mode === 'replace' ? photos : mergePhotos(prev, photos, mode);
            if (next.length > maxItems) {
              const overflow = next.length - maxItems;
              if (mode === 'prepend') {
                trimmedFromEnd = overflow;
                next = next.slice(0, maxItems);
              }
            }
            return next;
          });

          setPhotoTotal(total);
          let appliedStartOffset = newStartOffset + trimmedFromStart;
          let appliedNextOffset = Math.max(newNextOffset - trimmedFromEnd, appliedStartOffset);
          if (mode === 'replace') {
            appliedStartOffset = newStartOffset + trimmedFromStart;
            appliedNextOffset = Math.max(newEndOffset - trimmedFromEnd, appliedStartOffset);
          }
          setStartOffset(appliedStartOffset);
          setNextOffset(appliedNextOffset);

          if (hasMoreFlag !== null) {
            setHasMore(hasMoreFlag);
          } else if (hasTotal) {
            setHasMore(appliedNextOffset < total);
          } else {
            setHasMore(photos.length >= limit);
          }

          if (hasTotal) {
            setHasMorePrev(appliedStartOffset > 0);
          } else {
            setHasMorePrev(nextOffsetValue > 0);
          }
        } else {
          secureLogger.error('Photos API failed', { status: response.status });
        }
      } catch (error) {
        secureLogger.error('Failed to fetch photos', { error: 'Photos fetch failed' });
      } finally {
        if (requestId === listRequestIdRef.current) {
          if (mode === 'append') {
            setLoadingMore(false);
          } else if (mode === 'prepend') {
            setLoadingPrev(false);
          } else {
            setLoading(false);
          }
        }
        if (mode === 'append' || mode === 'prepend') {
          listScrollInFlightRef.current = false;
        }
      }
    },
    [mergePhotos, normalizePhotoList, pageSize, startOffset, nextOffset],
  );

  const ensureMediaCookie = useCallback(async (deviceId: string) => {
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        return;
      }
      const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/photos/media-cookie`;
      await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
    } catch (error) {
      secureLogger.error('Failed to issue media cookie', { error: 'Media cookie request failed' });
    }
  }, []);

  const fetchAnchor = useCallback(
    async (deviceId: string, at: string, size: number, range: DateRange | null) => {
      const requestId = ++anchorRequestIdRef.current;
      setLoading(true);
      try {
        const limit = size > 0 ? size : DEFAULT_PAGE_SIZE;
        lastAnchorKeyRef.current = `${deviceId}:${at}:${limit}:${range?.start ?? ''}:${range?.end ?? ''}`;
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();

        if (!token) {
          secureLogger.error('No token for photo anchor request');
          return;
        }

        const params = new URLSearchParams();
        params.set('at', at);
        params.set('limit', String(limit));
        if (range?.start && range?.end) {
          params.set('from_time', range.start);
          params.set('to_time', range.end);
        }
        const query = params.toString();
        const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/photos/anchor?${query}`;
        secureLogger.debug('Photo anchor request', { deviceId, at, limit, range });
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (requestId !== anchorRequestIdRef.current) {
            return;
          }
          const photos = normalizePhotoList(data.photos || []);
          const offset = typeof data.offset === 'number' ? data.offset : 0;
          const total = typeof data.total === 'number' ? data.total : offset + photos.length;
          const next = offset + photos.length;
          secureLogger.debug('Photo anchor response', {
            count: photos.length,
            offset,
            total,
          });
          const anchorTime = Date.parse(at);
          const closest = photos.reduce<{
            photo: PhotoData;
            distance: number;
          } | null>((best, photo) => {
            const raw = photo.captured_at || photo.timestamp;
            if (!raw) {
              return best;
            }
            const time = Date.parse(raw);
            if (!Number.isFinite(time)) {
              return best;
            }
            const distance = Number.isFinite(anchorTime) ? Math.abs(time - anchorTime) : 0;
            if (!best || distance < best.distance) {
              return { photo, distance };
            }
            return best;
          }, null);
          const closestAt = closest?.photo.captured_at || closest?.photo.timestamp || at;
          lastAnchorKeyRef.current = `${deviceId}:${closestAt}:${limit}:${range?.start ?? ''}:${range?.end ?? ''}`;
          setPhotoPageData(photos);
          setPhotoTotal(total);
          setStartOffset(offset);
          setNextOffset(next);
          setHasMore(next < total);
          setHasMorePrev(offset > 0);
          setScrollTarget(closestAt);
          setSelectedTimelineAt(closestAt);
        } else {
          secureLogger.error('Photo anchor API failed', { status: response.status });
        }
      } catch (error) {
        secureLogger.error('Failed to fetch photo anchor', { error: 'Anchor fetch failed' });
      } finally {
        if (requestId === anchorRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [normalizePhotoList],
  );

  const fetchTimeline = useCallback(
    async (deviceId: string, range: DateRange | null = null) => {
      try {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          secureLogger.error('No token for photo timeline request');
          return;
        }

        const params = new URLSearchParams();
        if (range?.start && range?.end) {
          params.set('from_time', range.start);
          params.set('to_time', range.end);
        }
        const query = params.toString();
        const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/photos/timeline${query ? `?${query}` : ''}`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data: PhotoTimelineResponse = await response.json();
          setTimeline(data);
        } else {
          secureLogger.error('Photo timeline API failed', { status: response.status });
        }
      } catch (error) {
        secureLogger.error('Failed to fetch photo timeline', { error: 'Timeline fetch failed' });
      }
    },
    [],
  );

  useEffect(() => {
    if (!activeDeviceId || isParamsInitialized) {
      return;
    }
    const initialRange = parseRangeFromParams();
    const initialSize = parseNumberParam('page_size', DEFAULT_PAGE_SIZE);
    setCurrentRange(initialRange);
    setPageSize(initialSize);
    setParamsInitialized(true);
  }, [activeDeviceId, isParamsInitialized, parseRangeFromParams, parseNumberParam]);

  useEffect(() => {
    if (!isParamsInitialized) {
      return;
    }
    const rangeFromUrl = parseRangeFromParams();
    const sizeFromUrl = parseNumberParam('page_size', DEFAULT_PAGE_SIZE);

    setCurrentRange((prev) => {
      const prevStart = prev?.start ?? null;
      const prevEnd = prev?.end ?? null;
      const nextStart = rangeFromUrl?.start ?? null;
      const nextEnd = rangeFromUrl?.end ?? null;
      if (prevStart === nextStart && prevEnd === nextEnd) {
        return prev;
      }
      return rangeFromUrl;
    });
    setPageSize((prev) => (prev === sizeFromUrl ? prev : sizeFromUrl));
  }, [searchParamString, isParamsInitialized, parseRangeFromParams, parseNumberParam]);

  useEffect(() => {
    if (!isParamsInitialized || !activeDeviceId) {
      return;
    }
    ensureMediaCookie(activeDeviceId);
    if (suppressReloadRef.current) {
      suppressReloadRef.current = false;
      return;
    }
    if (timelineModeRef.current || selectedTimelineAt) {
      return;
    }
    const reloadKey = `${activeDeviceId}:${currentRange?.start ?? ''}:${currentRange?.end ?? ''}`;
    if (reloadKeyRef.current === reloadKey && photoPageData.length > 0) {
      return;
    }
    reloadKeyRef.current = reloadKey;
    setScrollTarget(null);
    setSelectedTimelineAt(null);
    setPhotoPageData([]);
    setPhotoTotal(0);
    setHasMore(true);
    setHasMorePrev(false);
    setNextOffset(0);
    setStartOffset(0);
    fetchPhotoData(activeDeviceId, currentRange, 0, pageSize, 'replace');
  }, [activeDeviceId, currentRange, pageSize, isParamsInitialized, fetchPhotoData, photoPageData.length, selectedTimelineAt]);

  const handleLoadMore = useCallback(() => {
    if (!activeDeviceId || loadingMore || loadingPrev || loading || !hasMore) {
      return;
    }
    if (listScrollInFlightRef.current) {
      return;
    }
    if (Number.isFinite(photoTotal) && photoTotal > 0 && nextOffset >= photoTotal) {
      if (hasMore) {
        setHasMore(false);
      }
      return;
    }
    listScrollInFlightRef.current = true;
    fetchPhotoData(activeDeviceId, currentRange, nextOffset, pageSize, 'append');
  }, [
    activeDeviceId,
    loadingMore,
    loadingPrev,
    loading,
    hasMore,
    photoTotal,
    nextOffset,
    fetchPhotoData,
    currentRange,
    pageSize,
  ]);

  const handleLoadPrev = useCallback(() => {
    if (!activeDeviceId || loadingPrev || loadingMore || loading || !hasMorePrev) {
      return;
    }
    if (listScrollInFlightRef.current) {
      return;
    }
    if (startOffset <= 0) {
      if (hasMorePrev) {
        setHasMorePrev(false);
      }
      return;
    }
    const prevOffset = Math.max(startOffset - pageSize, 0);
    const container = scrollContainerRef.current;
    pendingScrollRef.current = container
      ? {
          scrollTop: container.scrollTop,
          scrollHeight: container.scrollHeight,
          container,
        }
      : {
          scrollTop: window.scrollY,
          scrollHeight: document.documentElement.scrollHeight,
          container: null,
        };
    listScrollInFlightRef.current = true;
    fetchPhotoData(activeDeviceId, currentRange, prevOffset, pageSize, 'prepend');
  }, [
    activeDeviceId,
    loadingPrev,
    loadingMore,
    loading,
    hasMorePrev,
    startOffset,
    pageSize,
    fetchPhotoData,
    currentRange,
  ]);

  useEffect(() => {
    if (loadingPrev) {
      return;
    }
    const pending = pendingScrollRef.current;
    if (!pending) {
      return;
    }
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      if (pending.container) {
        const nextHeight = pending.container.scrollHeight;
        const delta = nextHeight - pending.scrollHeight;
        if (delta > 0) {
          pending.container.scrollTop = pending.scrollTop + delta;
        }
        return;
      }
      const nextHeight = document.documentElement.scrollHeight;
      const delta = nextHeight - pending.scrollHeight;
      if (delta > 0) {
        window.scrollTo({ top: pending.scrollTop + delta });
      }
    });
  }, [loadingPrev, photoPageData.length]);

  const handleColumnWidthChange = useCallback((nextWidth: number) => {
    const clamped = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, nextWidth));
    setColumnWidth((prev) => (prev === clamped ? prev : clamped));
  }, []);

  useEffect(() => {
    if (!isParamsInitialized) {
      return;
    }
    const nextSize = calcPageSize(columnWidth);
    setPageSize((prev) => {
      if (prev === nextSize) {
        return prev;
      }
      updateQueryParams(currentRange, nextSize);
      return nextSize;
    });
  }, [columnWidth, currentRange, isParamsInitialized, updateQueryParams]);

  useEffect(() => {
    if (!isParamsInitialized) {
      return;
    }
    const handleResize = () => {
      const nextSize = calcPageSize(columnWidth);
      setPageSize((prev) => {
        if (prev === nextSize) {
          return prev;
        }
        updateQueryParams(currentRange, nextSize);
        return nextSize;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [columnWidth, currentRange, isParamsInitialized, updateQueryParams]);

  const handleTimelineSelect = useCallback(
    (at: string) => {
      if (!activeDeviceId) {
        return;
      }
      listRequestIdRef.current += 1;
      setLoading(false);
      setLoadingMore(false);
      setLoadingPrev(false);
      const normalizedAt = normalizeTimelineAnchor(at);
      timelineModeRef.current = true;
      suppressReloadRef.current = true;
      reloadKeyRef.current = `${activeDeviceId}::`;
      lastAnchorKeyRef.current = `${activeDeviceId}:${normalizedAt}:${pageSize}::`;
      setCurrentRange(null);
      if (isParamsInitialized) {
        updateQueryParams(null, pageSize);
      }
      setHasMore(false);
      setHasMorePrev(false);
      setNextOffset(0);
      setStartOffset(0);
      setSelectedTimelineAt(normalizedAt);
      fetchAnchor(activeDeviceId, normalizedAt, pageSize, null);
    },
    [activeDeviceId, fetchAnchor, pageSize, isParamsInitialized, updateQueryParams, normalizeTimelineAnchor],
  );

  useEffect(() => {
    if (!activeDeviceId || !selectedTimelineAt) {
      return;
    }
    const normalizedAt = normalizeTimelineAnchor(selectedTimelineAt);
    if (normalizedAt !== selectedTimelineAt) {
      setSelectedTimelineAt(normalizedAt);
      return;
    }
    const key = `${activeDeviceId}:${normalizedAt}:${pageSize}::`;
    if (lastAnchorKeyRef.current === key) {
      return;
    }
    fetchAnchor(activeDeviceId, normalizedAt, pageSize, null);
  }, [activeDeviceId, selectedTimelineAt, pageSize, fetchAnchor, normalizeTimelineAnchor]);

  useEffect(() => {
    setPhotoPageData([]);
    setPhotoTotal(0);
    setHasMore(true);
    setHasMorePrev(false);
    setNextOffset(0);
    setStartOffset(0);
    setTimeline(null);
    setScrollTarget(null);
    setSelectedTimelineAt(null);
    timelineModeRef.current = false;
    reloadKeyRef.current = null;
  }, [activeDeviceId]);

  useEffect(() => {
    if (!activeDeviceId) {
      return;
    }
    fetchTimeline(activeDeviceId, null);
  }, [activeDeviceId, fetchTimeline]);

  if (deviceNotFound) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="指定されたデバイスが見つかりません"
      >
        <Button type="primary" onClick={() => navigate('/devices')}>
          デバイス一覧へ
        </Button>
      </Empty>
    );
  }

  if (!activeDeviceId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="デバイスを選択してください"
      />
    );
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button 
          onClick={() => {
            if (activeDeviceId) {
              setPhotoPageData([]);
              setPhotoTotal(0);
              setHasMore(true);
              setNextOffset(0);
              if (selectedTimelineAt) {
                fetchAnchor(activeDeviceId, selectedTimelineAt, pageSize, null);
                return;
              }
              fetchPhotoData(activeDeviceId, currentRange, 0, pageSize, 'replace');
            }
          }}
          loading={loading}
        >
          更新
        </Button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <div className="photo-page-spin">
          <Spin spinning={loading} style={{ height: '100%' }}>
            <div style={{ height: '100%', minHeight: 0 }}>
              {(() => {
                const Gallery = PhotoGallery ?? PhotoGalleryComponent;
                return (
                  <Gallery
                    photoData={photoPageData}
                    total={photoTotal}
                    hasMore={hasMore}
                    hasMorePrev={hasMorePrev}
                    loadingPrev={loadingPrev}
                    loadingMore={loadingMore}
                    loadingInitial={loading}
                    onLoadPrev={handleLoadPrev}
                    onLoadMore={handleLoadMore}
                    timeline={timeline}
                    onTimelineSelect={handleTimelineSelect}
                    selectedTimelineAt={selectedTimelineAt}
                    scrollTarget={scrollTarget}
                    scrollContainerRef={scrollContainerRef}
                    columnWidth={columnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                  />
                );
              })()}
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default PhotoPage;
